import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {getDefaultVisualizationConfigGQL} from 'analysis-tool/ui/charting/getDefaultVisualizationConfig';
import {assert} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {
  GetSubsetDataFrame,
  GetSubsetDataFrameVariables,
} from 'data-frames/graphql/__generated/GetSubsetDataFrame';
import {GET_SUBSET_DATA_FRAME_QUERY, DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import PresentationalUniversalBlockView from 'insights/universal-blocks/PresentationalUniversalBlockView';
import Float from 'ui-library/floating-ui/Float';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {DEFAULT_FLOAT_PROPS, MAX_POPOVER_HEIGHT, MAX_POPOVER_WIDTH} from './PopoverConstants';

interface DataFramePopoverProps {
  children: JSX.Element;
  dataFrameId: string;
}

/**
 * Popover contents for a data frame or a table visualization.
 *
 * @package
 */
export function DataFramePopoverContents({
  dataFrameId,
}: Pick<DataFramePopoverProps, 'dataFrameId'>): JSX.Element {
  return (
    <BenchlingQuery<GetSubsetDataFrame, GetSubsetDataFrameVariables>
      query={GET_SUBSET_DATA_FRAME_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{dataFrameId, limit: DEFAULT_DATA_FRAME_ROW_LIMIT}}
    >
      {({dataFrame}) => {
        assert(!isInaccessible(dataFrame), 'Expected data frame to be accessible.');
        return (
          /*
           * A fixed height is required so tables don't expand and are forced to use their internal scrollbar.
           * However, we only apply the fixed height for larger data frames, so there isn't whitespace in the
           * popover beneath small tables.
           */
          <div className={css(styles.floatContent, dataFrame.numRows > 10 && styles.fixedHeight)}>
            <PresentationalUniversalBlockView
              actionOrigin={ActionOrigin.ANALYSIS_TOOL}
              inputDataFrame={dataFrame.dataFrameJson}
              visualizationConfig={getDefaultVisualizationConfigGQL(
                dataFrame.dataFrameJson.columns,
                VisualizationType.TABLE
              )}
              viewStateId={`analysis-flowchart-popover-${dataFrameId}`}
              paneProps={{title: dataFrame.dataFrameJson.name, toolbar: <></>}}
            />
          </div>
        );
      }}
    </BenchlingQuery>
  );
}

/**
 * Show a persisted data frame on hover.
 */
export default function DataFramePopover({dataFrameId, children}: DataFramePopoverProps): JSX.Element {
  return (
    <Float
      {...DEFAULT_FLOAT_PROPS}
      renderContent={() => <DataFramePopoverContents dataFrameId={dataFrameId} />}
    >
      {children}
    </Float>
  );
}

const styles = StyleSheet.create({
  fixedHeight: {
    height: MAX_POPOVER_HEIGHT,
  },
  floatContent: {
    maxWidth: MAX_POPOVER_WIDTH,
  },
});
